import type { ReactNode } from "react";
import { SkyText } from "@skypointcloud/skypoint-ui";
import type { Reliability } from "@shared/types/reliability";
import { ReliabilityHelpLink, successRateText } from "@/components/reliability/Freshness";
import { useReliability } from "@/hooks/use-reliability";
import { HealthIcon } from "@/lib/icons";
import { ROUTES } from "@/lib/routes";
import { OverviewCard, OverviewLink, statusTone } from "./OverviewCard";

/**
 * The Overview's Reliability card (#828, `docs/requirements/slo-canary.md` R6.3): the share of the
 * last 7 days' imports that succeeded, the late connections and the failing schedules, from
 * skyData's own run records (`GET /instances/current/reliability`). Not gated on the skyMonitor
 * flag: it needs no skyMonitor. A failed read says so in the card and leaves the others alone.
 */

/** How many names a list in the card shows before "and N more". */
const NAMES_SHOWN = 3;

export interface ReliabilitySummary {
  runs: number;
  succeeded: number;
  rate: number | null;
  late: string[];
  failing: string[];
}

/**
 * The card's figures: every connection's counted runs of the window added up (an import of three
 * connections counts three times, as on Connections), the Late connections, and the enabled
 * schedules whose last run failed or succeeded with a warning.
 */
export function reliabilitySummary(data: Reliability): ReliabilitySummary {
  const runs = data.connections.reduce((sum, c) => sum + c.runs, 0);
  const succeeded = data.connections.reduce((sum, c) => sum + c.succeeded, 0);
  return {
    runs,
    succeeded,
    rate: runs === 0 ? null : succeeded / runs,
    late: data.connections.filter((c) => c.freshness === "late").map((c) => c.displayName),
    // An enabled schedule only: a disabled one's last failure is history.
    failing: data.schedules
      .filter(
        (s) => s.enabled && (s.lastOutcome === "failed" || s.lastOutcome === "partially_succeeded"),
      )
      .map((s) => s.name),
  };
}

/** "Sales, HR and 2 more". */
export function namesText(names: readonly string[]): string {
  const shown = names.slice(0, NAMES_SHOWN);
  const more = names.length - shown.length;
  if (more > 0) return `${shown.join(", ")} and ${more} more`;
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} and ${shown.at(-1)}`;
}

/**
 * #1229: what the rate counts, beside the Runs card's count of whole runs, so the two can be
 * reconciled: each connection's part of every import that finished succeeded, failed or partly
 * failed. A cancelled run (a force reset with no failed step reads Cancelled, #1231), a
 * transformation-only run and a connection whose part neither failed nor loaded (its load
 * skipped) are not counted, and the Runs card names its cancelled runs (#1211).
 */
export const RELIABILITY_COUNTED =
  "Counts each connection in every finished import, so an import of two connections counts twice. Cancelled runs, transformation-only runs and a connection whose load was skipped are not counted.";

function countSentence(count: number, one: string, many: string): string {
  return count === 1 ? `1 ${one}` : `${count.toLocaleString()} ${many}`;
}

export function ReliabilityCard() {
  const reliability = useReliability("7d");
  const summary = reliability.data ? reliabilitySummary(reliability.data) : null;

  function body(): ReactNode {
    if (reliability.isPending) {
      return (
        <SkyText size={300} color="muted" block>
          Loading
        </SkyText>
      );
    }
    if (reliability.isError || !summary) {
      return (
        <SkyText size={300} color="muted" block testId="overview-reliability-error">
          Reliability could not be read.
        </SkyText>
      );
    }
    return (
      <div className="space-y-2">
        <SkyText size={300} weight="semibold" block testId="overview-reliability-rate">
          {summary.rate === null
            ? "No connection imports finished in the last 7 days."
            : `${successRateText(summary.rate)} of connection imports succeeded in the last 7 days (${summary.succeeded.toLocaleString()} of ${summary.runs.toLocaleString()}).`}
        </SkyText>
        {summary.rate === null ? null : (
          <SkyText size={200} color="muted" block testId="overview-reliability-counted">
            {RELIABILITY_COUNTED}
          </SkyText>
        )}
        <SkyText size={300} block testId="overview-reliability-late">
          {summary.late.length === 0
            ? "No connection is late."
            : `${countSentence(summary.late.length, "connection is late", "connections are late")}: ${namesText(summary.late)}.`}
        </SkyText>
        <SkyText size={300} block testId="overview-reliability-failing">
          {summary.failing.length === 0
            ? "No schedule's last run failed."
            : `${countSentence(summary.failing.length, "schedule's last run failed or partly failed", "schedules' last runs failed or partly failed")}: ${namesText(summary.failing)}.`}
        </SkyText>
        <ReliabilityHelpLink screen="overview" testId="overview-reliability-help" />
      </div>
    );
  }

  return (
    <OverviewCard
      id="reliability"
      title="Reliability"
      icon={HealthIcon}
      tone={
        summary
          ? statusTone({
              failed: summary.failing.length,
              warning: summary.late.length,
              ok: summary.runs,
            })
          : "info"
      }
      testId="overview-reliability"
      action={<OverviewLink to={ROUTES.connections}>Open Connections</OverviewLink>}
    >
      {body()}
    </OverviewCard>
  );
}
